import {
  PREVIEWABLE_FILE_TYPES,
  FILE_CATEGORIES,
  MAX_ATTACHMENT_NAME_LIMIT
} from '../constants/fileConstants'

/**
 * Get file extension from filename
 * @param {string} filename - The filename
 * @returns {string} - The file extension (lowercase)
 */
export const getFileExtension = (filename: string): string => {
  if (!filename) return ''
  const lastDotIndex = filename.lastIndexOf('.')
  return lastDotIndex !== -1 ? filename.slice(lastDotIndex).toLowerCase() : ''
}

/**
 * Determine file category based on extension
 * @param {string} filename - The filename
 * @returns {string} - The file category
 */
export const getFileCategory = (filename: string): string => {
  const extension = getFileExtension(filename)

  if (PREVIEWABLE_FILE_TYPES.IMAGE.includes(extension)) {
    return FILE_CATEGORIES.IMAGE
  }
  if (PREVIEWABLE_FILE_TYPES.VIDEO.includes(extension)) {
    return FILE_CATEGORIES.VIDEO
  }
  if (PREVIEWABLE_FILE_TYPES.AUDIO.includes(extension)) {
    return FILE_CATEGORIES.AUDIO
  }
  if (PREVIEWABLE_FILE_TYPES.PDF.includes(extension)) {
    return FILE_CATEGORIES.PDF
  }
  if (PREVIEWABLE_FILE_TYPES.TEXT.includes(extension)) {
    return FILE_CATEGORIES.TEXT
  }
  if (['.doc', '.docx'].includes(extension)) {
    return FILE_CATEGORIES.DOCUMENT
  }
  if (['.xls', '.xlsx'].includes(extension)) {
    return FILE_CATEGORIES.SPREADSHEET
  }
  if (['.ppt', '.pptx'].includes(extension)) {
    return FILE_CATEGORIES.PRESENTATION
  }

  return FILE_CATEGORIES.OTHER
}

/**
 * Create a blob URL for preview
 * @param {Blob} blob - The file blob
 * @returns {string} - The blob URL
 */
export const createBlobUrl = (blob: Blob): string => {
  return URL.createObjectURL(blob)
}

/**
 * Revoke a blob URL to free memory
 * @param {string} url - The blob URL to revoke
 */
export const revokeBlobUrl = (url: string): void => {
  if (url && url.startsWith('blob:')) {
    URL.revokeObjectURL(url)
  }
}

/**
 * Get truncated filename for display
 * @param {string} fileName - The filename
 * @returns {string} - Truncated filename
 */
export const getTruncatedFileName = (fileName: string): string => {
  if (fileName?.length <= MAX_ATTACHMENT_NAME_LIMIT) return fileName
  return `${fileName?.slice(0, 15)}...${fileName?.slice(-15)}`
}

/**
 * Generate a screenshot filename with timestamp
 * @returns {string} - Screenshot filename
 */
export const generateScreenshotFilename = (): string => {
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
  return `screenshot-${timestamp}.png`
}
